import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  PlusIcon, 
  PencilAltIcon, 
  TrashIcon, 
  SearchIcon,
  FilterIcon,
  XIcon,
  CloudUploadIcon,
  PhotographIcon,
  DocumentTextIcon
} from '@heroicons/react/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  deleteDoc,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
  limit,
  orderBy,
  startAfter,
  endBefore,
  limitToLast
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { updateProfileDoc } from '../../services/apiRoutes';

const collectionMap = {
  "Berita": "news",
  "Kegiatan": "activities",
  "Perangkat Gampong": "staff",
  "Layanan": "reports",
  "Geografi": "geography",
  "SDM": "sdm",
  "SDG": "sdg",
  "Pemerintahan": "governance",
  "Visi Misi": "visimisi",
  "Sejarah": "history",
  "Pencapaian": "achievements",
  "Qanun": "qanun",
  "Dokumen": "documents",
  "Gallery": "gallery"
};

export default function CMSContentPage({ title }) {
  const { slug } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [villageId, setVillageId] = useState(null);
  
  // New Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // New Fields for Audit Alignment
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Perangkat Gampong specific fields
  const [jabatan, setJabatan] = useState("");
  const [telepon, setTelepon] = useState("");
  const [emailStaff, setEmailStaff] = useState("");

  // Pagination States
  const [page, setPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const PAGE_SIZE = 10;

  // Configuration based on title
  const config = {
    hasImage: ["Berita", "Kegiatan", "Perangkat Gampong", "Pencapaian", "Pemerintahan", "Gallery"].includes(title),
    useRte: ["Berita", "Kegiatan", "Visi Misi", "Sejarah", "Pencapaian"].includes(title),
    hasFile: ["Dokumen", "Qanun", "Berita", "Kegiatan"].includes(title),
    hasCategory: ["Berita", "Kegiatan", "Dokumen", "Pencapaian"].includes(title),
    isReadOnly: title === "Layanan",
    hasDates: title === "Pemerintahan"
  };

  const fetchData = async (direction = 'initial') => {
    if (!villageId && direction !== 'initial') return;
    setLoading(true);
    try {
      let vId = villageId;
      if (!vId) {
        const villageQ = query(collection(db, "villages"), where("slug", "==", slug));
        const villageSnap = await getDocs(villageQ);
        if (villageSnap.empty) {
          const newVillage = await addDoc(collection(db, "villages"), {
            slug,
            name: slug.replace(/-/g, ' ').toUpperCase()
          });
          vId = newVillage.id;
          setVillageId(vId);
        } else {
          vId = villageSnap.docs[0].id;
          setVillageId(vId);
        }
      }

      const collName = collectionMap[title] || title.toLowerCase();
      let q;
      const baseQuery = collection(db, `villages/${vId}/${collName}`);

      if (direction === 'next' && lastVisible) {
        q = query(baseQuery, orderBy("createdAt", "desc"), startAfter(lastVisible), limit(PAGE_SIZE));
      } else if (direction === 'prev' && firstVisible) {
        q = query(baseQuery, orderBy("createdAt", "desc"), endBefore(firstVisible), limitToLast(PAGE_SIZE));
      } else {
        q = query(baseQuery, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
      }

      const qSnapshot = await getDocs(q);
      const items = qSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Set First and Last Visible for next/prev
      if (qSnapshot.docs.length > 0) {
        setFirstVisible(qSnapshot.docs[0]);
        setLastVisible(qSnapshot.docs[qSnapshot.docs.length - 1]);
        
        // Check if there is a next page
        const nextQ = query(baseQuery, orderBy("createdAt", "desc"), startAfter(qSnapshot.docs[qSnapshot.docs.length - 1]), limit(1));
        const nextSnap = await getDocs(nextQ);
        setHasNext(!nextSnap.empty);
      } else {
        setHasNext(false);
      }

      setData(items);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setFirstVisible(null);
    setLastVisible(null);
    fetchData('initial');
    
    // Fetch categories if needed
    if (config.hasCategory && villageId) {
      const catColl = title === "Berita" ? "news_categories" : 
                    title === "Kegiatan" ? "activity_categories" :
                    title === "Dokumen" ? "document_categories" : "achievement_categories";
      getDocs(collection(db, `villages/${villageId}/${catColl}`)).then(snap => {
        setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, title, villageId]);

  const handleNextPage = () => {
    if (hasNext) {
      setPage(prev => prev + 1);
      fetchData('next');
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
      fetchData('prev');
    }
  };

  const handleOpenAddModal = () => {
    setEditingItem(null);
    setNewTitle("");
    setNewContent("");
    setImageUrl("");
    setFileUrl("");
    setJabatan("");
    setTelepon("");
    setEmailStaff("");
    setCategoryId("");
    setStartDate("");
    setEndDate("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setNewTitle(item.title || item.name || item.judul || "");
    setNewContent(item.content || item.deskripsi || "");
    setImageUrl(item.imageUrl || item.url_gambar || "");
    setFileUrl(item.fileUrl || "");
    setJabatan(item.jabatan || (item.perangkat_desa_obj?.jabatan) || (item.jabatan_obj?.nama) || "");
    setTelepon(item.telepon || "");
    setEmailStaff(item.email || "");
    setCategoryId(item.categoryId || "");
    setStartDate(item.tgl_mulai || "");
    setEndDate(item.tgl_akhir || "");
    setIsModalOpen(true);
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG with 0.7 quality
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          resolve(dataUrl);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size for documents (Non-image files aren't compressed)
    if (file.size > 800000) {
      toast.error("File dokumen terlalu besar! Maksimal 800KB.");
      return;
    }

    setIsUploadingFile(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFileUrl(reader.result);
        setIsUploadingFile(false);
        toast.success("File siap disimpan!");
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Gagal memproses file");
      setIsUploadingFile(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      toast.info("Sedang memproses & mengompres gambar...");
      const compressedBase64 = await compressImage(file);
      
      // Safety check for compressed size
      if (compressedBase64.length > 1000000) {
        toast.error("Gambar terlalu kompleks dan besar bahkan setelah dikompresi. Silakan gunakan gambar lain.");
        setIsUploading(false);
        return;
      }

      setImageUrl(compressedBase64);
      toast.success("Gambar berhasil dikompresi & siap simpan!");
    } catch (error) {
      console.error("Error compressing image:", error);
      toast.error("Gagal memproses gambar");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newTitle) return;
    setIsSubmitting(true);
    
    try {
      const collName = collectionMap[title] || title.toLowerCase();
      
      if (editingItem) {
        // UPDATE MODE
        const docRef = doc(db, `villages/${villageId}/${collName}`, editingItem.id);
        await updateDoc(docRef, {
          title: newTitle,
          content: newContent,
          imageUrl: imageUrl,
          fileUrl: fileUrl,
          jabatan: jabatan,
          telepon: telepon,
          email: emailStaff,
          categoryId: categoryId,
          tgl_mulai: startDate,
          tgl_akhir: endDate,
          updatedAt: serverTimestamp()
        });

        // Consistency sync for single-doc items
        if (title === "Sejarah") {
          await updateProfileDoc(slug, "sejarah", { deskripsi: newContent, url_gambar: imageUrl });
        } else if (title === "Visi Misi") {
          await updateProfileDoc(slug, "visimisi", { visi: newTitle, misi: newContent, url_gambar: imageUrl });
        }

        setData(data.map(item => item.id === editingItem.id ? {
          ...item,
          title: newTitle,
          content: newContent,
          imageUrl: imageUrl,
          fileUrl: fileUrl
        } : item));
        
        toast.success("Data berhasil diperbarui!");
      } else {
        // ADD MODE
        const docRef = await addDoc(collection(db, `villages/${villageId}/${collName}`), {
          title: newTitle,
          content: newContent,
          imageUrl: imageUrl,
          fileUrl: fileUrl,
          jabatan: jabatan,
          telepon: telepon,
          email: emailStaff,
          categoryId: categoryId,
          tgl_mulai: startDate,
          tgl_akhir: endDate,
          status: "Published",
          date: new Date().toISOString().split('T')[0],
          createdAt: serverTimestamp()
        });

        // Consistency sync
        if (title === "Sejarah") {
          await updateProfileDoc(slug, "sejarah", { deskripsi: newContent, url_gambar: imageUrl });
        } else if (title === "Visi Misi") {
          await updateProfileDoc(slug, "visimisi", { visi: newTitle, misi: newContent, url_gambar: imageUrl });
        }
        
        const newItem = {
          id: docRef.id,
          title: newTitle,
          content: newContent,
          imageUrl: imageUrl,
          fileUrl: fileUrl,
          status: "Published",
          date: new Date().toISOString().split('T')[0]
        };
        
        setData([newItem, ...data]);
        toast.success("Data berhasil ditambahkan!");
      }
      
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Gagal menyimpan data: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsSubmitting(true);
    
    try {
      const collName = collectionMap[title] || title.toLowerCase();
      await deleteDoc(doc(db, `villages/${villageId}/${collName}`, itemToDelete.id));
      setData(data.filter(item => item.id !== itemToDelete.id));
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success("Data berhasil dihapus!");
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Gagal menghapus data: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Manajemen {title}</h2>
            <p className="mt-1 text-sm text-gray-500">Kelola dan atur data {title} desa Anda.</p>
          </div>
          {!config.isReadOnly && (
            <button 
              onClick={handleOpenAddModal}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-bold rounded-2xl shadow-lg shadow-primary/20 text-white bg-primary hover:bg-primary/90 transition-all duration-300 transform hover:scale-105"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Tambah {title}
            </button>
          )}
        </div>

        <div className="bg-white shadow-sm border border-gray-100 rounded-3xl overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
             <div className="relative max-w-xs w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input 
                  type="text" 
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-100 bg-gray-50/50 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all transition-duration-300" 
                  placeholder={`Cari ${title}...`} 
                />
             </div>
             <div className="flex items-center space-x-2">
                <button className="inline-flex items-center px-4 py-2 border border-gray-100 rounded-xl text-sm font-bold text-gray-600 bg-white hover:bg-gray-50 transition-colors">
                   <FilterIcon className="w-4 h-4 mr-2" />
                   Filter
                </button>
             </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Judul/Nama
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Tanggal Update
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  <tr>
                     <td colSpan="4" className="px-6 py-10 text-center text-gray-400">Loading data...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                     <td colSpan="4" className="px-6 py-10 text-center text-gray-400">Tidak ada data ditemukan.</td>
                  </tr>
                ) : data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center">
                        {item.imageUrl && (
                          <div className="h-10 w-10 flex-shrink-0 mr-3">
                            <img className="h-10 w-10 rounded-lg object-cover border border-gray-100" src={item.imageUrl} alt="" />
                          </div>
                        )}
                        <div className="text-sm font-bold text-gray-900 group-hover:text-primary transition-colors flex items-center">
                          {item.title || item.name || item.judul || item.id}
                          {item.fileUrl && (
                            <a 
                              href={item.fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-2 p-1 text-blue-500 hover:bg-blue-50 rounded-md transition-colors"
                              title="Lihat/Download File"
                            >
                              <DocumentTextIcon className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {item.date || item.createdAt?.toDate()?.toLocaleDateString() || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${
                        item.status === 'Published' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                      }`}>
                        {item.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                         {!config.isReadOnly ? (
                           <>
                             <button 
                              onClick={() => handleOpenEditModal(item)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                             >
                                <PencilAltIcon className="w-5 h-5" />
                             </button>
                             <button 
                              onClick={() => handleOpenDeleteModal(item)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                             >
                                <TrashIcon className="w-5 h-5" />
                             </button>
                           </>
                         ) : (
                           <button 
                            onClick={() => handleOpenEditModal(item)}
                            className="p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                            title="Detail Pesan"
                           >
                              <SearchIcon className="w-5 h-5" />
                           </button>
                         )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="px-6 py-4 bg-gray-50/30 border-t border-gray-100 flex items-center justify-between">
             <span className="text-xs text-gray-500">Halaman {page}</span>
             <div className="flex space-x-2">
                <button 
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className={`px-4 py-1.5 border rounded-lg text-xs font-bold transition-all ${
                    page === 1 ? 'bg-white text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Sebelumnya
                </button>
                <button 
                  onClick={handleNextPage}
                  disabled={!hasNext}
                  className={`px-4 py-1.5 border rounded-lg text-xs font-bold transition-all ${
                    !hasNext ? 'bg-white text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Selanjutnya
                </button>
             </div>
          </div>
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}</style>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
            </div>
            
            {/* Modal Content */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
            <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full animate-modalIn">
              <form onSubmit={handleSave}>
                <div className="bg-white px-8 pt-8 pb-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-extrabold text-gray-900">
                      {editingItem ? `Edit ${title}` : `Tambah ${title} Baru`}
                    </h3>
                    <button 
                      type="button" 
                      onClick={() => setIsModalOpen(false)}
                      className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Image Upload Field - Conditional */}
                    {config.hasImage && (
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Gambar Utama / Thumbnail</label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-100 border-dashed rounded-2xl bg-gray-50/50 hover:bg-gray-100/50 transition-colors cursor-pointer relative overflow-hidden group">
                          {imageUrl ? (
                            <div className="relative w-full aspect-video rounded-xl overflow-hidden">
                              <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <PhotographIcon className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1 text-center">
                              <CloudUploadIcon className="mx-auto h-12 w-12 text-gray-300" />
                              <div className="flex text-sm text-gray-600">
                                <label className="relative cursor-pointer rounded-md font-bold text-primary hover:text-primary/80">
                                  <span>Pilih gambar</span>
                                  <input type="file" className="sr-only" accept="image/*" onChange={handleImageUpload} disabled={isUploading} />
                                </label>
                                <p className="pl-1 text-gray-500 text-xs">atau seret dan lepas</p>
                              </div>
                              <p className="text-xs text-gray-400">PNG, JPG, GIF hingga 2MB</p>
                            </div>
                          )}
                          {isUploading && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                              <div className="flex flex-col items-center">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                                <span className="text-xs font-bold text-gray-600">Mengunggah...</span>
                              </div>
                            </div>
                          )}
                          <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={handleImageUpload} disabled={isUploading} />
                        </div>
                      </div>
                    )}

                    {/* File Upload Field - Conditional for Documents */}
                    {config.hasFile && (
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Upload Dokumen (PDF, DOCX, dll)</label>
                        <div className="mt-1 flex justify-center px-6 py-8 border-2 border-gray-100 border-dashed rounded-2xl bg-gray-50/50 hover:bg-gray-100/50 transition-colors cursor-pointer relative overflow-hidden group text-center">
                          {fileUrl ? (
                            <div className="flex flex-col items-center">
                              <DocumentTextIcon className="w-12 h-12 text-blue-500 mb-2" />
                              <span className="text-sm font-bold text-gray-700 truncate max-w-xs">{fileUrl.split('/').pop()}</span>
                              <button type="button" onClick={() => setFileUrl("")} className="mt-2 text-xs text-red-600 hover:underline">Hapus & Ganti</button>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <CloudUploadIcon className="mx-auto h-12 w-12 text-gray-300" />
                              <div className="flex text-sm justify-center">
                                <label className="relative cursor-pointer rounded-md font-bold text-primary hover:text-primary/80">
                                  <span>Pilih file dokumen</span>
                                  <input type="file" className="sr-only" onChange={handleFileUpload} disabled={isUploadingFile} />
                                </label>
                              </div>
                              <p className="text-xs text-gray-400">PDF, DOC, DOCX, XLS up to 10MB</p>
                            </div>
                          )}
                          {isUploadingFile && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {config.hasCategory && (
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Kategori</label>
                        <select
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          className="block w-full px-4 py-3.5 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        >
                          <option value="">Pilih Kategori</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.nama || cat.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {config.hasDates && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Tanggal Mulai Jabatan</label>
                          <input 
                            type="date" 
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="block w-full px-4 py-3 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Tanggal Akhir Jabatan</label>
                          <input 
                            type="date" 
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="block w-full px-4 py-3 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Judul / Nama</label>
                      <input 
                        type="text" 
                        required
                        readOnly={config.isReadOnly}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        className="block w-full px-4 py-3.5 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="Masukkan judul..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">{config.isReadOnly ? 'Isi Pesan' : 'Konten / Deskripsi'}</label>
                      <div className="bg-gray-50/50 rounded-2xl overflow-hidden border border-gray-100">
                        {config.useRte && !config.isReadOnly ? (
                          <ReactQuill 
                            theme="snow" 
                            value={newContent} 
                            onChange={setNewContent}
                            placeholder="Tulis konten di sini..."
                            className="bg-white"
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                ['link', 'clean']
                              ],
                            }}
                          />
                        ) : (
                          <textarea 
                            rows="5"
                            value={newContent}
                            readOnly={config.isReadOnly}
                            onChange={(e) => setNewContent(e.target.value)}
                            className="block w-full px-4 py-3.5 border-none bg-white text-sm focus:outline-none focus:ring-0 transition-all font-medium"
                            placeholder="Masukkan deskripsi..."
                          />
                        )}
                      </div>
                    </div>

                    {title === "Perangkat Gampong" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Jabatan</label>
                          <input 
                            type="text" 
                            value={jabatan}
                            onChange={(e) => setJabatan(e.target.value)}
                            className="block w-full px-4 py-3 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                            placeholder="Contoh: Keuchik"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">No. Telepon</label>
                          <input 
                            type="text" 
                            value={telepon}
                            onChange={(e) => setTelepon(e.target.value)}
                            className="block w-full px-4 py-3 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                            placeholder="0812..."
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Email</label>
                          <input 
                            type="email" 
                            value={emailStaff}
                            onChange={(e) => setEmailStaff(e.target.value)}
                            className="block w-full px-4 py-3 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                            placeholder="email@gampong.id"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50/80 px-8 py-6 flex flex-row-reverse space-x-2 space-x-reverse border-t border-gray-100">
                  {!config.isReadOnly && (
                    <button 
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex justify-center py-3.5 px-8 border border-transparent shadow-xl shadow-primary/20 text-sm font-bold rounded-2xl text-white bg-primary hover:bg-primary/90 focus:outline-none transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                    >
                      {isSubmitting ? "Menyimpan..." : (editingItem ? "Update Data" : "Simpan Data")}
                    </button>
                  )}
                  <button 
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="inline-flex justify-center py-3.5 px-8 border border-gray-200 shadow-sm text-sm font-bold rounded-2xl text-gray-600 bg-white hover:bg-gray-50 focus:outline-none transition-all"
                  >
                    {config.isReadOnly ? 'Tutup' : 'Batal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <style>{`
            @keyframes modalIn {
              from { opacity: 0; transform: scale(0.95) translateY(10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
            .animate-modalIn {
              animation: modalIn 0.3s ease-out forwards;
            }
            .ql-container {
              border-bottom-left-radius: 1rem;
              border-bottom-right-radius: 1rem;
              border: none !important;
              min-height: 200px;
              font-family: inherit;
            }
            .ql-toolbar {
              border-top-left-radius: 1rem;
              border-top-right-radius: 1rem;
              border: none !important;
              border-bottom: 1px solid #f3f4f6 !important;
              background: #f9fafb;
            }
            .ql-editor {
              font-size: 0.875rem;
              line-height: 1.5;
            }
          `}</style>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-[110] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsDeleteModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
            <div className="inline-block align-middle bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full animate-modalIn">
              <div className="bg-white px-8 pt-8 pb-6 text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mb-2">Hapus {title}?</h3>
                <p className="text-sm text-gray-500">
                  Apakah Anda yakin ingin menghapus data <strong>"{itemToDelete?.title || itemToDelete?.name || itemToDelete?.judul}"</strong>? Tindakan ini tidak dapat dibatalkan.
                </p>
              </div>
              <div className="bg-gray-50/80 px-8 py-6 flex flex-col space-y-2 border-t border-gray-100">
                <button 
                  onClick={confirmDelete}
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center py-3.5 px-4 border border-transparent shadow-lg shadow-red-200 text-sm font-bold rounded-2xl text-white bg-red-600 hover:bg-red-700 transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
                >
                  {isSubmitting ? "Menghapus..." : "Ya, Hapus Sekarang"}
                </button>
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full inline-flex justify-center py-3.5 px-4 border border-gray-200 text-sm font-bold rounded-2xl text-gray-600 bg-white hover:bg-gray-50 transition-all"
                >
                  Batalkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
